import {
  createLocalizeCrossSiteHref,
  createLocalizeHref,
  createLocalizeSignupHref,
} from "@marketing-sites/l10n/localize";
import {
  CROSS_APP_PATHS,
  isCrossAppHref,
} from "@marketing-sites/utils/cross-app-href";

import { localeConfig } from "./config";
import { isLaunchedLocalizedRoute, isLocalizableRoute } from "./routes";

/**
 * Keeps an in-app link inside the locale it is rendered in. Safe to apply to
 * any href: external URLs, anchors, and routes this app does not serve under
 * [locale] are returned unchanged, so call sites need no per-link judgement.
 *
 * Server components pass the `locale` route param; client components should use
 * `useLocalizedHref` instead of reading the prefix themselves.
 */
export const localizeHref = createLocalizeHref({
  config: localeConfig,
  // Legal content links to in-app routes by absolute URL (both with and without
  // the www host), so those have to be recognised as internal or they stay on
  // the canonical route.
  sameSiteOrigins: ["https://www.intercom.com", "https://intercom.com"],
  isLocalizableRoute,
  isLaunchedLocalizedRoute,
  isExcludedPath: isCrossAppHref,
});

/**
 * Keeps a link to fin.ai in the locale the reader is already in.
 * `localizeHref` declines these — they are cross-domain, so this app's route
 * tables say nothing about them — which would drop a localised page's readers
 * onto English.
 *
 * fin.ai owns its own LOCALIZABLE/LAUNCHED gates, so a prefix it has not
 * launched 307s to canonical: right destination, one extra redirect. Preferred
 * over encoding fin.ai's route table here, where it would silently rot.
 *
 * `/signup` is the exception: a separate React app behind the CDN, so no locale
 * middleware runs on it and there is no redirect to fall back on. Its URLs are
 * also matched by exact pathname in
 * EXTERNAL_ROUTES_THAT_SHOULD_INCLUDE_UTM_PARAMS, so a prefix would drop
 * utm_referrer and lose signup attribution.
 */
export const localizeCrossSiteHref = createLocalizeCrossSiteHref({
  config: localeConfig,
  crossSiteOrigins: ["https://www.fin.ai", "https://fin.ai"],
  isExcludedPath: isCrossAppHref,
});

/**
 * Carries the reader's locale into signup, which neither localizer above can
 * reach: signup is a separate app, so its URLs must stay unprefixed. See
 * `createLocalizeSignupHref`. `/admins/*` is the monolith's own signup.
 */
export const localizeSignupHref = createLocalizeSignupHref({
  config: localeConfig,
  signupPaths: [...CROSS_APP_PATHS, "/admins/sign_up", "/admins/signup"],
});
